import React, { useState, useEffect, useCallback } from 'react';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import { RadioChangeEvent } from 'antd/es/radio/interface';
import { StyledNewListingDeliveryConditionsForm } from './style';
import EnumRadioGroup from '../../../admin/Selects/EnumRadioGroup';
import {
  DeliveryConditionsForm,
  DetailedDeliveryForm,
  FreeFormDeliveryForm,
} from './DeliveryConditionForms';
import {
  DetailedDeliveryData,
  NewListingDeliveryConditionsFormProps,
  DeliveryType,
  DeliveryConditionsData,
  DeliveryProductReference,
  ListingProduct,
} from '../../../types/ListingData';

export const NewListingDeliveryConditionsForm = ({
  deliveryConditionsData,
  isEditing = false,
  onDetailedDeliveriesChange,
  existingDetailedDeliveries = [],
  availableProducts = [],
  onDeliveryDataChange,
}: NewListingDeliveryConditionsFormProps) => {
  const form = useFormInstance();
  const [selectedDeliveryType, setSelectedDeliveryType] =
    useState<DeliveryType | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const [deliveryData, setDeliveryData] = useState<{
    delivery_conditions?: DeliveryConditionsData;
    detailed_delivery?: DetailedDeliveryData[];
    free_form?: DeliveryConditionsData;
  }>({});

  useEffect(() => {
    const conditions = Array.isArray(deliveryConditionsData)
      ? deliveryConditionsData
      : deliveryConditionsData
        ? [deliveryConditionsData]
        : [];

    if (conditions.length === 0 && !isInitialized) {
      setSelectedDeliveryType('delivery_conditions');
      form.setFieldValue('delivery_type', 'delivery_conditions');
      setDeliveryData({});
      setIsInitialized(true);
      return;
    }

    if (conditions.length > 0) {
      const firstCondition = conditions[0];
      setSelectedDeliveryType(firstCondition.delivery_type);
      form.setFieldValue('delivery_type', firstCondition.delivery_type);

      const existingData = { ...deliveryData };
      if (firstCondition.delivery_type === 'delivery_conditions') {
        existingData.delivery_conditions =
          firstCondition as DeliveryConditionsData;
      } else if (firstCondition.delivery_type === 'free_form') {
        existingData.free_form = firstCondition as DeliveryConditionsData;
      } else if (firstCondition.delivery_type === 'detailed_delivery') {
        const detailedDeliveries = conditions.map((condition) => {
          const selectedProducts =
            availableProducts && availableProducts.length > 0
              ? ((condition.products || [])
                  .map((productRef) => {
                    const deliveryRef = productRef as DeliveryProductReference;
                    const productId =
                      deliveryRef.listing_product_id || deliveryRef.id;
                    const categoryId = deliveryRef.category_id;

                    let fullProduct: ListingProduct | null = null;

                    if (productId) {
                      fullProduct =
                        availableProducts.find((p) => p.id === productId) ||
                        null;
                    }

                    if (!fullProduct && categoryId) {
                      fullProduct =
                        availableProducts.find(
                          (p) => p.category_id === categoryId
                        ) || null;
                    }

                    return fullProduct;
                  })
                  .filter(Boolean) as ListingProduct[])
              : [];

          const productQuantities: { [categoryId: number]: number } = {};
          if (availableProducts && availableProducts.length > 0) {
            (condition.products || []).forEach((productRef) => {
              const deliveryRef = productRef as DeliveryProductReference;
              const productId =
                deliveryRef.listing_product_id || deliveryRef.id;
              const categoryId = deliveryRef.category_id;
              const deliveryQuantity = deliveryRef.delivery_quantity
                ? parseFloat(deliveryRef.delivery_quantity.toString())
                : 0;

              if (deliveryQuantity <= 0) return;

              const fullProduct = productId
                ? availableProducts.find((p) => p.id === productId)
                : categoryId
                  ? availableProducts.find((p) => p.category_id === categoryId)
                  : undefined;

              const key = fullProduct?.category_id ?? categoryId;
              if (key) {
                productQuantities[key] = deliveryQuantity;
              }
            });
          }

          return {
            id: condition.id,
            delivery_address: condition.delivery_address || '',
            delivery_start_date: condition.delivery_start_date,
            delivery_end_date: condition.delivery_end_date,
            is_all_products: condition.is_all_products || false,
            is_full_quantity: condition.is_full_quantity || false,
            selected_products: selectedProducts,
            product_quantities: productQuantities,
            products: condition.products || [],
            delivery_frequency_value: condition.delivery_frequency_value,
            delivery_frequency_unit: condition.delivery_frequency_unit,
          };
        });
        existingData.detailed_delivery =
          detailedDeliveries as DetailedDeliveryData[];
      }
      setDeliveryData(existingData);
      setIsInitialized(true);
    }
  }, [deliveryConditionsData, isEditing, form, availableProducts]);

  useEffect(() => {
    if (existingDetailedDeliveries.length > 0) {
      setDeliveryData((prev) => ({
        ...prev,
        detailed_delivery: existingDetailedDeliveries,
      }));
    }
  }, [existingDetailedDeliveries]);

  const handleDeliveryTypeChange = (e: RadioChangeEvent) => {
    const newType = e.target.value as DeliveryType;

    form.setFieldValue('delivery_type', newType);
    setSelectedDeliveryType(newType);

    const currentDeliveryData = getCurrentDeliveryData();
    onDeliveryDataChange?.(currentDeliveryData);
  };

  const handleDetailedDeliveriesChange = (
    deliveries: DetailedDeliveryData[]
  ) => {
    const updatedData = {
      ...deliveryData,
      detailed_delivery: deliveries,
    };
    setDeliveryData(updatedData);
    onDetailedDeliveriesChange?.(deliveries);

    const currentDeliveryData = deliveries.map(
      (delivery): DeliveryConditionsData => ({
        id: delivery.id,
        delivery_type: 'detailed_delivery',
        delivery_address: delivery.delivery_address,
        delivery_start_date: delivery.delivery_start_date,
        delivery_end_date: delivery.delivery_end_date,
        is_all_products: delivery.is_all_products,
        is_full_quantity: delivery.is_full_quantity,
        products: (delivery.products || []) as DeliveryProductReference[],
        delivery_frequency_value: delivery.delivery_frequency_value,
        delivery_frequency_unit: delivery.delivery_frequency_unit,
      })
    );
    onDeliveryDataChange?.(currentDeliveryData);
  };

  const handleDeliveryConditionsChange = (formData: DeliveryConditionsData) => {
    const updatedData = {
      ...deliveryData,
      delivery_conditions: formData,
    };
    setDeliveryData(updatedData);

    const currentDeliveryData = getCurrentDeliveryData();
    onDeliveryDataChange?.(currentDeliveryData);
  };

  const handleFreeFormChange = useCallback(
    (formData: DeliveryConditionsData) => {
      const updatedData = {
        ...deliveryData,
        free_form: formData,
      };
      setDeliveryData(updatedData);

      const currentDeliveryData = getCurrentDeliveryData();
      onDeliveryDataChange?.(currentDeliveryData);
    },
    [onDeliveryDataChange]
  );

  const renderDeliveryForm = () => {
    switch (selectedDeliveryType) {
      case 'delivery_conditions':
        return (
          <DeliveryConditionsForm
            key="delivery_conditions"
            formData={
              deliveryData.delivery_conditions ||
              (Array.isArray(deliveryConditionsData)
                ? deliveryConditionsData[0]
                : deliveryConditionsData)
            }
            isEditing={isEditing}
            onFormDataChange={handleDeliveryConditionsChange}
          />
        );
      case 'detailed_delivery':
        return (
          <DetailedDeliveryForm
            key="detailed_delivery"
            onDeliveriesChange={handleDetailedDeliveriesChange}
            existingDeliveries={
              deliveryData.detailed_delivery || existingDetailedDeliveries
            }
            availableProducts={availableProducts}
          />
        );
      case 'free_form':
        return (
          <FreeFormDeliveryForm
            key="free_form"
            formData={
              deliveryData.free_form ||
              (Array.isArray(deliveryConditionsData)
                ? deliveryConditionsData[0]
                : deliveryConditionsData)
            }
            isEditing={isEditing}
            onFormDataChange={handleFreeFormChange}
          />
        );
      default:
        return null;
    }
  };

  const getCurrentDeliveryData = (): DeliveryConditionsData[] => {
    const currentFormData = form.getFieldsValue(true);
    const currentType = currentFormData.delivery_type;

    const originalCondition = Array.isArray(deliveryConditionsData)
      ? deliveryConditionsData[0]
      : deliveryConditionsData;
    const originalId = originalCondition?.id
      ? Number(originalCondition.id)
      : undefined;

    if (currentType === 'delivery_conditions') {
      return [
        {
          id: originalId,
          delivery_type: 'delivery_conditions',
          is_all_products: false,
          is_full_quantity: false,
          contract_period_type: currentFormData.contract_period_type,
          contract_start_date: currentFormData.contract_start_date,
          contract_end_date: currentFormData.contract_end_date,
          delivery_period_value: currentFormData.delivery_period_value,
          delivery_period_unit: currentFormData.delivery_period_unit,
          delivery_frequency_value: currentFormData.delivery_frequency_value,
          delivery_frequency_unit: currentFormData.delivery_frequency_unit,
        },
      ];
    } else if (currentType === 'free_form') {
      return [
        {
          id: originalId,
          delivery_type: 'free_form',
          is_all_products: false,
          is_full_quantity: false,
          free_form_description: currentFormData.free_form_description,
          contract_start_date: currentFormData.contract_start_date,
          contract_end_date: currentFormData.contract_end_date,
        },
      ];
    } else if (currentType === 'detailed_delivery') {
      const detailedDeliveries = deliveryData.detailed_delivery || [];
      return detailedDeliveries.map(
        (delivery): DeliveryConditionsData => ({
          id: delivery.id,
          delivery_type: 'detailed_delivery',
          delivery_address: delivery.delivery_address,
          delivery_start_date: delivery.delivery_start_date,
          delivery_end_date: delivery.delivery_end_date,
          is_all_products: delivery.is_all_products,
          is_full_quantity: delivery.is_full_quantity,
          products: (delivery.products || []) as DeliveryProductReference[],
          delivery_frequency_value: delivery.delivery_frequency_value,
          delivery_frequency_unit: delivery.delivery_frequency_unit,
        })
      );
    }

    return [];
  };

  return (
    <StyledNewListingDeliveryConditionsForm>
      <div className="delivery-type-selection">
        <EnumRadioGroup
          name="delivery_type"
          code="ListingDeliveryType"
          onChange={handleDeliveryTypeChange}
        />
      </div>

      {selectedDeliveryType && (
        <div className="delivery-form-content">{renderDeliveryForm()}</div>
      )}
    </StyledNewListingDeliveryConditionsForm>
  );
};
